/**
 * story-configurator.js — Studio 1/1
 * Phase 3C: Story Configurator JS module.
 *
 * ADR-007 Architecture:
 *   Upload  : Uploadcare Widget v3 (browser → Uploadcare CDN, no backend)
 *   Product : Shopify variant per size; add-ons are separate line items
 *   Prices  : Client-side display only; enforced by Shopify variant prices at checkout
 *   State   : sessionStorage['studio1of1:storyConfig'] (cleared on successful checkout)
 *
 * Responsibilities:
 *   1. Read category from URL ?category= param and display it in the heading
 *   2. Load Uploadcare widget dynamically; handle file upload events
 *   3. Build image thumbnail preview grid with remove buttons
 *   4. Track selected size via [data-size-card] ARIA radio pattern
 *   5. Live price recalculation on every option change
 *   6. Character counter for description textarea
 *   7. Validate: at least 1 upload required, size selection required
 *   8. On "Review Order": save config to sessionStorage → redirect to review page
 *   9. On page load: restore any saved config from sessionStorage (back button support)
 *
 * Hooks consumed (see main-story-configurator.liquid for full contract):
 *   [data-configurator]      section root (source of data-* config attributes)
 *   [data-upload-key]        Uploadcare public key
 *   [data-upload-zone]       Uploadcare widget mount target
 *   [data-upload-input]      hidden input Uploadcare attaches to
 *   [data-upload-preview]    thumbnail grid container
 *   [data-upload-count]      count paragraph
 *   [data-upload-error]      upload validation error
 *   [data-size-card]         size option buttons (role="radio")
 *   [data-size-value]        size key on each card
 *   [data-size-price]        price in pence on each card (data attr)
 *   [data-selected-size]     hidden input — current size key
 *   [data-selected-variant]  hidden input — current variant ID
 *   [data-size-error]        size validation error
 *   [data-description]       project notes textarea (single field — matches prototype)
 *   [data-gift-wrap]         gift wrap checkbox
 *   [data-gift-card]         gift card checkbox
 *   [data-packaging]         packaging checkbox
 *   [data-price-base]        base price display
 *   [data-price-addon-row]   individual add-on summary row
 *   [data-price-total]       total price display
 *   [data-submit-review]     Review Order button
 *   [data-config-error]      section-level aria-live error
 *   [data-category-label]    heading eyebrow updated from URL param
 *
 * sessionStorage key: 'studio1of1:storyConfig'
 * sessionStorage schema:
 * {
 *   category:        string          // from URL ?category=
 *   uploadUrls:      string[]        // Uploadcare CDN URLs
 *   uploadFileNames: string[]        // original filenames
 *   size:            string          // 'small'|'medium'|'large'|'xl'
 *   variantId:       string          // Shopify variant ID for size
 *   description:     string
 *   giftWrap:        boolean
 *   giftCard:        boolean
 *   packaging:       boolean
 *   giftWrapVariantId:  string|null
 *   giftCardVariantId:  string|null
 *   packagingVariantId: string|null
 *   priceBase:       number          // in pence
 *   priceTotal:      number          // in pence
 * }
 */

(() => {
  'use strict';

  var STORAGE_KEY      = 'studio1of1:storyConfig';
  var UPLOADCARE_CDN   = 'https://ucarecdn.com/libs/widget/3.x/uploadcare.full.min.js';
  var ACTIVE_CLASS     = 'is-selected';
  var LOADING_CLASS    = 'is-loading';

  // ── Boot ───────────────────────────────────────────────────────────────────
  var root = document.querySelector('[data-configurator]');
  if (!root) return;

  // ── Read section data attributes ───────────────────────────────────────────
  var uploadKey    = root.dataset.uploadKey || '';
  var maxImages    = parseInt(root.dataset.maxImages || '5', 10);
  var reviewUrl    = root.dataset.reviewUrl || '/pages/story-review';

  var prices = {
    small:    parseInt(root.dataset.priceSmall   || '4500', 10),
    medium:   parseInt(root.dataset.priceMedium  || '6500', 10),
    large:    parseInt(root.dataset.priceLarge   || '7500', 10),
    xl:       parseInt(root.dataset.priceXl      || '8500', 10),
    xxl:      parseInt(root.dataset.priceXxl     || '10000', 10),
    giftWrap: parseInt(root.dataset.priceGiftWrap || '1500', 10),
    giftCard: parseInt(root.dataset.priceGiftCard || '800',  10),
    packaging:parseInt(root.dataset.pricePackaging|| '2800', 10),
  };

  var variantIds = {
    small:     root.dataset.variantSmall    || '',
    medium:    root.dataset.variantMedium   || '',
    large:     root.dataset.variantLarge    || '',
    xl:        root.dataset.variantXl       || '',
    xxl:       root.dataset.variantXxl      || '',
    giftWrap:  root.dataset.variantGiftWrap || '',
    giftCard:  root.dataset.variantGiftCard || '',
    packaging: root.dataset.variantPackaging|| '',
  };

  // ── DOM refs ───────────────────────────────────────────────────────────────
  var uploadZone     = root.querySelector('[data-upload-zone]');
  var uploadInput    = root.querySelector('[data-upload-input]');
  var uploadPreview  = root.querySelector('[data-upload-preview]');
  var uploadCount    = root.querySelector('[data-upload-count]');
  var uploadError    = root.querySelector('[data-upload-error]');
  var sizeCards      = Array.from(root.querySelectorAll('[data-size-card]'));
  var selectedSize   = root.querySelector('[data-selected-size]');
  var selectedVariant= root.querySelector('[data-selected-variant]');
  var sizeError      = root.querySelector('[data-size-error]');
  var description    = root.querySelector('[data-description]');
  var giftWrap       = root.querySelector('[data-gift-wrap]');
  var giftCard       = root.querySelector('[data-gift-card]');
  var packaging      = root.querySelector('[data-packaging]');
  var priceBase      = root.querySelector('[data-price-base]');
  var priceTotal     = root.querySelector('[data-price-total]');
  var submitBtn      = root.querySelector('[data-submit-review]');
  var configError    = root.querySelector('[data-config-error]');
  var categoryLabel  = root.querySelector('[data-category-label]');
  var summarySize    = root.querySelector('[data-summary-size]');
  var summaryThumb   = root.querySelector('[data-price-summary-thumb]');

  // ── State ──────────────────────────────────────────────────────────────────
  var uploadedFiles = []; // [{ url, name }]

  // ── 1. Category label from URL param ──────────────────────────────────────
  (function initCategory() {
    var params   = new URLSearchParams(window.location.search);
    var category = params.get('category') || '';
    if (categoryLabel && category) {
      // Capitalise first letter; replace hyphens with spaces
      var label = category.replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
      categoryLabel.textContent = 'Create Your ' + label + ' Story';
    }

    // GA4: story_category_view — fires once when the configurator loads
    window.Studio1of1 && window.Studio1of1.track('story_category_view', {
      story_category: category || 'unknown',
    });
  })();

  // ── 2. Uploadcare widget ───────────────────────────────────────────────────
  function initUploadcare() {
    if (!uploadKey || !uploadInput) return;

    // Set Uploadcare globals before script loads
    window.UPLOADCARE_PUBLIC_KEY   = uploadKey;
    window.UPLOADCARE_TABS         = 'file camera';
    window.UPLOADCARE_MULTIPLE     = true;
    window.UPLOADCARE_MULTIPLE_MAX = maxImages;
    window.UPLOADCARE_IMAGES_ONLY  = true;
    window.UPLOADCARE_PREVIEW_STEP = true;
    window.UPLOADCARE_LOCALE       = 'en';
    window.UPLOADCARE_STORE        = 'manual'; // files stored when order confirmed

    var script = document.createElement('script');
    script.src = UPLOADCARE_CDN;
    script.charset = 'utf-8';
    script.async = true;
    script.onload = function() {
      var widget = window.uploadcare && window.uploadcare.Widget(uploadInput);
      if (!widget) return;

      // Widget loaded — hide manual zone UI (widget has its own UI)
      if (uploadZone) {
        var inner = uploadZone.querySelector('.config-upload-zone__inner');
        if (inner) inner.hidden = true;
      }

      widget.onChange(function(fileGroup) {
        if (!fileGroup) {
          uploadedFiles = [];
          renderPreviews();
          return;
        }
        // fileGroup.files() returns array of file promises
        var promises = fileGroup.files();
        if (!promises || !promises.length) {
          uploadedFiles = [];
          renderPreviews();
          return;
        }

        Promise.all(promises.map(function(f) { return f; })).then(function(files) {
          uploadedFiles = files
            .filter(function(f) { return f && f.cdnUrl; })
            .map(function(f) {
              return { url: f.cdnUrl, name: f.name || 'image' };
            });
          clearError(uploadError);
          renderPreviews();
          recalcPrice();
        }).catch(function(err) {
          console.error('[story-configurator] Uploadcare error:', err);
          showError(uploadError, 'Upload failed. Please try again.');
        });
      });
    };
    document.head.appendChild(script);
  }

  // ── 3. Image preview grid ──────────────────────────────────────────────────
  function renderPreviews() {
    if (!uploadPreview) return;

    if (uploadZone) uploadZone.classList.toggle('has-file', uploadedFiles.length > 0);
    if (summaryThumb) {
      summaryThumb.innerHTML = uploadedFiles.length
        ? '<img src="' + escHtml(uploadedFiles[0].url + '-/preview/112x112/-/quality/lighter/') + '" alt="" width="56" height="56" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;">'
        : '';
    }

    if (!uploadedFiles.length) {
      uploadPreview.innerHTML = '';
      if (uploadCount) uploadCount.hidden = true;
      return;
    }

    var html = uploadedFiles.map(function(file, i) {
      var thumbUrl = file.url + '-/preview/200x200/-/quality/lighter/';
      return [
        '<div class="upload-thumb" data-upload-thumb>',
          '<img src="' + escHtml(thumbUrl) + '" alt="' + escHtml(file.name) + '" width="100" height="100" loading="lazy">',
          '<button type="button" class="upload-thumb__remove" aria-label="Remove ' + escHtml(file.name) + '" data-remove-thumb="' + i + '">',
            '<span aria-hidden="true">&times;</span>',
          '</button>',
        '</div>',
      ].join('');
    }).join('');

    uploadPreview.innerHTML = html;

    // Wire remove buttons
    uploadPreview.querySelectorAll('[data-remove-thumb]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(btn.dataset.removeThumb, 10);
        uploadedFiles.splice(idx, 1);
        renderPreviews();
        recalcPrice();
      });
    });

    if (uploadCount) {
      var n = uploadedFiles.length;
      uploadCount.textContent = n + ' image' + (n === 1 ? '' : 's') + ' uploaded';
      uploadCount.hidden = false;
    }
  }

  // ── 4. Size selection (ARIA radio pattern) ─────────────────────────────────
  sizeCards.forEach(function(card) {
    card.addEventListener('click', function() { selectSize(card); });
    card.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectSize(card);
      }
      // Arrow key navigation within radio group
      var cards = sizeCards;
      var idx   = cards.indexOf(card);
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        var next = cards[(idx + 1) % cards.length];
        next.focus();
        selectSize(next);
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        var prev = cards[(idx - 1 + cards.length) % cards.length];
        prev.focus();
        selectSize(prev);
      }
    });
  });

  function selectSize(card) {
    sizeCards.forEach(function(c) {
      c.classList.remove(ACTIVE_CLASS);
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });
    card.classList.add(ACTIVE_CLASS);
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    var sizeKey = card.dataset.sizeValue;
    if (selectedSize)   selectedSize.value   = sizeKey;
    if (selectedVariant) selectedVariant.value = variantIds[sizeKey] || '';

    if (summarySize) {
      var labelEl = card.querySelector('.option-card__label');
      var dimsEl  = card.querySelector('.option-card__dims');
      summarySize.textContent = (labelEl ? labelEl.textContent : '') + ' · ' + (dimsEl ? dimsEl.textContent : '');
    }

    clearError(sizeError);
    recalcPrice();
  }

  // ── 5. Live price recalculation ────────────────────────────────────────────
  function recalcPrice() {
    var sizeKey = selectedSize ? selectedSize.value : '';
    var base    = sizeKey ? (prices[sizeKey] || 0) : 0;
    var addons  = 0;

    if (giftWrap  && giftWrap.checked)  addons += prices.giftWrap;
    if (giftCard  && giftCard.checked)  addons += prices.giftCard;
    if (packaging && packaging.checked) addons += prices.packaging;

    var total = base + addons;

    // Update base price display
    if (priceBase) {
      priceBase.textContent = base > 0 ? formatMoney(base) : '—';
    }

    // Update add-on rows
    ['gift_wrap', 'gift_card', 'packaging'].forEach(function(key) {
      var row     = root.querySelector('[data-price-addon-row="' + key + '"]');
      var checkEl = root.querySelector('[data-' + key.replace('_', '-') + ']');
      if (row) row.hidden = !(checkEl && checkEl.checked);
    });

    // Update total
    if (priceTotal) {
      priceTotal.textContent = total > 0 ? formatMoney(total) : '—';
    }
  }

  // Wire all option changes to recalcPrice
  [giftWrap, giftCard, packaging].forEach(function(el) {
    if (el) el.addEventListener('change', recalcPrice);
  });

  // ── 7. Validation ──────────────────────────────────────────────────────────
  function validate() {
    var valid = true;

    // Upload required
    if (!uploadedFiles.length) {
      showError(uploadError, 'Please upload at least one reference image.');
      valid = false;
    } else {
      clearError(uploadError);
    }

    // Size required
    if (!selectedSize || !selectedSize.value) {
      showError(sizeError, 'Please select a size.');
      valid = false;
    } else {
      clearError(sizeError);
    }

    if (!valid && configError) {
      showError(configError, 'Please complete the required steps above before continuing.');
      configError.hidden = false;
      // Focus first error
      var firstErr = root.querySelector('[data-upload-error]:not([hidden]), [data-size-error]:not([hidden])');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (configError) {
      configError.hidden = true;
      configError.textContent = '';
    }

    return valid;
  }

  function showError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function clearError(el) {
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  // ── 8. Save config to sessionStorage + redirect to review ──────────────────
  if (submitBtn) {
    submitBtn.addEventListener('click', function() {
      if (!validate()) return;

      submitBtn.classList.add(LOADING_CLASS);
      submitBtn.disabled = true;

      var params  = new URLSearchParams(window.location.search);
      var category = params.get('category') || '';

      var sizeKey = selectedSize ? selectedSize.value : '';
      var base    = sizeKey ? (prices[sizeKey] || 0) : 0;
      var addons  = 0;
      if (giftWrap  && giftWrap.checked)  addons += prices.giftWrap;
      if (giftCard  && giftCard.checked)  addons += prices.giftCard;
      if (packaging && packaging.checked) addons += prices.packaging;

      var config = {
        category:           category,
        uploadUrls:         uploadedFiles.map(function(f) { return f.url; }),
        uploadFileNames:    uploadedFiles.map(function(f) { return f.name; }),
        size:               sizeKey,
        variantId:          (selectedVariant ? selectedVariant.value : '') || '',
        description:        (description ? description.value.trim() : ''),
        giftWrap:           !!(giftWrap && giftWrap.checked),
        giftCard:           !!(giftCard && giftCard.checked),
        packaging:          !!(packaging && packaging.checked),
        giftWrapVariantId:  variantIds.giftWrap  || null,
        giftCardVariantId:  variantIds.giftCard  || null,
        packagingVariantId: variantIds.packaging || null,
        priceBase:          base,
        priceTotal:         base + addons,
        savedAt:            Date.now(),
      };

      // GA4: configurator_submit — fires when customer proceeds to review page
      window.Studio1of1 && window.Studio1of1.track('configurator_submit', {
        story_category:  config.category || 'unknown',
        story_size:      config.size,
        currency:        window.Studio1of1.shop && window.Studio1of1.shop.currency || 'USD',
        value:           config.priceTotal / 100,
        upload_count:    config.uploadUrls.length,
        gift_wrap:       config.giftWrap,
        gift_card:       config.giftCard,
        packaging:       config.packaging,
      });

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      } catch (e) {
        // sessionStorage unavailable (private browsing, storage full)
        console.warn('[story-configurator] sessionStorage unavailable:', e);
        // Encode minimal config in URL as fallback
        var fallback = encodeURIComponent(JSON.stringify({
          size: config.size,
          category: config.category,
          priceTotal: config.priceTotal,
        }));
        window.location.href = reviewUrl + '?cfg=' + fallback;
        return;
      }

      window.location.href = reviewUrl;
    });
  }

  // ── 9. Restore config from sessionStorage (back-button support) ────────────
  (function restoreConfig() {
    var saved;
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      saved = JSON.parse(raw);
    } catch (e) { return; }

    // Restore upload previews (URLs only — Uploadcare widget state cannot be
    // restored, but thumbnails are rendered from saved URLs)
    if (saved.uploadUrls && saved.uploadUrls.length) {
      uploadedFiles = saved.uploadUrls.map(function(url, i) {
        return { url: url, name: (saved.uploadFileNames && saved.uploadFileNames[i]) || 'image' };
      });
      renderPreviews();
    }

    // Restore size
    if (saved.size) {
      var card = root.querySelector('[data-size-value="' + saved.size + '"]');
      if (card) selectSize(card);
    }

    // Restore description
    if (description && saved.description) {
      description.value = saved.description;
    }

    // Restore options
    if (giftWrap  && saved.giftWrap)  giftWrap.checked  = true;
    if (giftCard  && saved.giftCard)  giftCard.checked  = true;
    if (packaging && saved.packaging) packaging.checked = true;

    recalcPrice();
  })();

  // ── Initialise Uploadcare ──────────────────────────────────────────────────
  initUploadcare();

  // ── Initialise size cards (first card not selected by default — user must choose) ──
  sizeCards.forEach(function(card, i) {
    card.setAttribute('tabindex', i === 0 ? '0' : '-1');
  });

  // Initial price calc (all zeros until size selected)
  recalcPrice();

  // ── Utility: money formatting ──────────────────────────────────────────────
  function formatMoney(pence) {
    // Use shop currency from Studio1of1 global if available
    var currency = (window.Studio1of1 && window.Studio1of1.shop && window.Studio1of1.shop.currency) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
      }).format(pence / 100);
    } catch (e) {
      return '$' + (pence / 100).toFixed(0);
    }
  }

  // ── Utility: HTML escape ───────────────────────────────────────────────────
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

})();
